// =============================================================================
// ui/shell/TradeActions.tsx — the toolbar's Import and Add Trade buttons.
// -----------------------------------------------------------------------------
// ── 🚨 WHY THESE ARE IN THE TOOLBAR AND NOT ONLY ON THE TRADES TAB ──
//
// Both screens were already built — the blank trade form is what the Trades tab's
// own "+ Add Trade" opens, and the import dropzone is a section further down that
// same tab. Neither was reachable from anywhere else.
//
// So on the Dashboard — the landing screen, the FIRST thing anyone sees, and the
// fallback tab — you could not log a trade or import one. You had to already know
// to go to Trades. The old client puts both in the header, one click from every
// tab. Josh spotted the gap from a side-by-side screenshot; see #51.
//
// ⚠ THE CAPABILITY WAS NEVER MISSING, ONLY THE ROUTE TO IT. That is why this is
// a small file and not a port: nothing here builds a form or parses a CSV.
//
// ── ⚠ THESE NAVIGATE. THE OLD CLIENT OPENS A MODAL IN PLACE ──
//
// Beta's `btn-add-trade` opens the trade form over whatever tab you are on, and
// `openCsvImport()` builds a modal the same way. Matching that here would mean
// hoisting the form out of the Trades tab into the shell, and the form's save
// path has to refresh whatever is underneath it — the same "reload path threading
// through both tabs" that keeps the Dashboard's rows from opening the editor.
//
// ▶ So these move the user to Trades and open the thing there. It is a real
// behavioural difference from beta and it is written down rather than glossed:
// the user lands on the tab where the new trade will actually appear, which is
// where they would have had to go anyway. If the form is ever hoisted, delete the
// navigation and keep the buttons.
// =============================================================================

/** What the Trades tab should do the moment it mounts. */
export type TradesIntent = 'add' | 'import';

export function TradeActions(
  { onIntent }: { onIntent: (intent: TradesIntent) => void },
) {
  return (
    <>
      {/* ⚠ Beta's order: Import then Add Trade, with Add Trade LAST and the only
          primary button in the bar (index.html:3573-3579). The primary is the
          action a new user needs, so it is the one that gets the colour. */}
      <button
        type="button"
        className="btn"
        onClick={() => onIntent('import')}
        title="Import a CSV — Sierra Chart, ATAS, Tradovate or Rithmic"
      >
        {/* Inline SVG, matching the old client's icon rather than a font glyph. */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          />
          <polyline
            points="17 8 12 3 7 8"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          />
          <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Import
      </button>

      <button
        type="button"
        className="btn primary"
        onClick={() => onIntent('add')}
        title="Log a trade by hand"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        Add Trade
      </button>
    </>
  );
}
